/**
 * MeetingDnaReview — TASK-474.
 * Client-DNA proposals derived from a meeting are NOT auto-written to the KG; they wait
 * here until an RM approves them (Laws L1/L2). Apply → upsert + verify; Skip → discard.
 * Mirrors the VoiceNoteWidget DNA-review flow, reusing ExtractionProposal.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, AlertCircle, Dna } from 'lucide-react';
import { toast } from 'sonner';

import { httpClient } from '../../../lib/httpClient';
import { approveFields } from '../../../services/knowledgeGraphService';
import { useClientDna } from '../../../hooks/useClientDna';
import ExtractionProposal, {
  type ProposalItem,
} from '../../kyc/workspace/ExtractionProposal';

interface MeetingDnaReviewProps {
  sessionId: string;
  clientId: string;
  // Rendered when there are no pending proposals. Default keeps the self-hiding (null) behaviour
  // for inline hosts; a tab host passes a message so the tab body is never blank.
  emptyFallback?: ReactNode;
}

interface DnaProposalsResponse {
  session_id: string;
  proposals: Array<{ field_path: string; value: unknown; confidence?: number; source?: string }>;
}

const proposalText = (value: unknown): string | undefined =>
  (value as { text?: string } | null)?.text;

export default function MeetingDnaReview({ sessionId, clientId, emptyFallback }: MeetingDnaReviewProps) {
  // DNA is shared (deduped across widgets) and only hides already-applied items → optional.
  const dnaQuery = useClientDna(clientId);
  // Session proposals are this widget's own required source.
  const sessionQuery = useQuery({
    queryKey: ['meeting-dna-proposals', sessionId],
    queryFn: () => httpClient.get<DnaProposalsResponse>(`/api/meeting/${sessionId}/dna-proposals`),
    enabled: !!sessionId,
  });
  // ponytail: field_path membership; assumes one mount per session (PostMeetingHub keys by session).
  const [removed, setRemoved] = useState<Set<string>>(() => new Set());

  const loading = sessionQuery.isLoading || dnaQuery.isLoading;

  // Compute only once BOTH settle, so an already-verified proposal never flashes before it's hidden.
  // run.results is immutable, so re-opening re-serves applied items — filter those whose text already
  // matches a verified DNA item (no new table needed).
  const base = useMemo<ProposalItem[]>(() => {
    if (sessionQuery.isLoading || dnaQuery.isLoading || !sessionQuery.data) return [];
    const dna = dnaQuery.data;
    const verified = new Set(
      dna
        ? [...dna.values, ...dna.exclusions, ...dna.tilts, ...dna.promises, ...dna.life_events]
            .filter((it) => it.verified)
            .map((it) => it.text)
        : []
    );
    return (sessionQuery.data.proposals ?? [])
      .filter((p) => {
        const t = proposalText(p.value);
        return !(t && verified.has(t));
      })
      .map((p) => ({ field_path: p.field_path, value: p.value, confidence: p.confidence ?? 0.5 }));
  }, [sessionQuery.isLoading, sessionQuery.data, dnaQuery.isLoading, dnaQuery.data]);

  const proposals = base.filter((p) => !removed.has(p.field_path));

  const drop = (fieldPath: string) => setRemoved((s) => new Set(s).add(fieldPath));

  const apply = async (fieldPath: string, value: unknown) => {
    try {
      await approveFields(clientId, [{ field_path: fieldPath, value, source: 'meeting_dna_delta' }]);
      drop(fieldPath);
      toast.success('Added to client DNA');
    } catch {
      toast.error('Could not apply DNA item');
    }
  };

  const applyAll = async () => {
    const fields = proposals.map((p) => ({
      field_path: p.field_path,
      value: p.value,
      source: 'meeting_dna_delta',
    }));
    if (!fields.length) return;
    try {
      const n = await approveFields(clientId, fields);
      setRemoved((s) => {
        const next = new Set(s);
        for (const f of fields) next.add(f.field_path);
        return next;
      });
      toast.success(`Added ${n} DNA item${n === 1 ? '' : 's'}`);
    } catch {
      toast.error('Could not apply DNA items');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading DNA proposals…
      </div>
    );
  }
  if (sessionQuery.isError) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-red-600">
        <AlertCircle className="h-4 w-4" /> Could not load DNA proposals.
        <button onClick={() => void sessionQuery.refetch()} className="underline">
          Retry
        </button>
      </div>
    );
  }
  if (proposals.length === 0) return <>{emptyFallback ?? null}</>; // nothing pending → fallback or self-hide

  return (
    <div className="space-y-2">
      <h4 className="flex items-center gap-2 text-sm font-medium text-gray-700">
        <Dna className="h-4 w-4 text-purple-600" /> Client DNA from this meeting ({proposals.length})
      </h4>
      <p className="text-xs text-gray-500">
        Derived from the conversation — review before adding to the client profile.
      </p>
      <ExtractionProposal proposals={proposals} onApply={apply} onApplyAll={applyAll} onSkip={drop} />
    </div>
  );
}
